// src/app/api/regionApi.ts

import { baseApi } from "./baseApi";
import type { UserRole } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES — FULL REGION/PROVINCE RECORDS (admin + programme use)
───────────────────────────────────────────────────────────────── */

export interface RegionUserLite {
  id: string;
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole | "ryp" | "pyp";
  region?: string | null;
  province?: string | null;
  ministryRole?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProvinceRecord {
  id: string;
  name: string;
  region?: string | null;
  pyp?: RegionUserLite | null;
  isArchived: boolean;
  archivedAt?: string | null;
  createdBy?: RegionUserLite | null;
  updatedBy?: RegionUserLite | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegionRecord {
  id: string;
  name: string;
  address: string;
  ryp?: RegionUserLite | null;
  provinces: ProvinceRecord[];
  isArchived: boolean;
  archivedAt?: string | null;
  createdBy?: RegionUserLite | null;
  updatedBy?: RegionUserLite | null;
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   CONVENIENCE ALIASES — used by OnboardingModal and event forms
   A RegionOption is a RegionRecord with a derived hasProvinces flag.
   A ProvinceOption is the minimal shape the modal needs.
───────────────────────────────────────────────────────────────── */

/** Slim shape used by OnboardingModal step 1. */
export interface RegionOption {
  id: string;
  name: string;
  /** True when the region has at least one active (non-archived) province. */
  hasProvinces: boolean;
}

/** Slim shape used by OnboardingModal step 2. */
export interface ProvinceOption {
  id: string;
  name: string;
}

/** Derive a RegionOption from a full RegionRecord. */
export const toRegionOption = (r: RegionRecord): RegionOption => ({
  id: r.id,
  name: r.name,
  hasProvinces: Array.isArray(r.provinces) && r.provinces.length > 0,
});

/** Derive a ProvinceOption from a full ProvinceRecord. */
export const toProvinceOption = (p: ProvinceRecord): ProvinceOption => ({
  id: p.id,
  name: p.name,
});

/* ─────────────────────────────────────────────────────────────────
   RESPONSE SHAPES
───────────────────────────────────────────────────────────────── */

interface RegionListResponse {
  success: boolean;
  message: string;
  count: number;
  data: RegionRecord[];
}

interface RegionItemResponse {
  success: boolean;
  message: string;
  data: RegionRecord;
}

interface RypUsersResponse {
  success: boolean;
  message: string;
  count: number;
  data: RegionUserLite[];
}

interface ProvinceListResponse {
  success: boolean;
  message: string;
  count: number;
  data: ProvinceRecord[];
}

/** Slim response from the /public endpoint (no auth, no PYP detail). */
interface PublicProvinceListResponse {
  success: boolean;
  count: number;
  data: ProvinceOption[];
}

interface ProvinceItemResponse {
  success: boolean;
  message: string;
  data: ProvinceRecord;
}

interface OnboardingResponse {
  success: boolean;
  data: {
    id: string;
    region: string;
    province: string | null;
    interests: string[];
    hasCompletedOnboarding: boolean;
    hasSeenWelcome: boolean;
  };
}

interface DismissWelcomeResponse {
  success: boolean;
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const regionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Regions ──────────────────────────────────────────────────────

    getRegions: builder.query<
      RegionListResponse,
      { archived?: boolean } | void
    >({
      query: (params) => ({
        url: "/regions",
        params:
          params && typeof params.archived === "boolean"
            ? { archived: String(params.archived) }
            : undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Region" as const, id })),
              { type: "Region" as const, id: "LIST" },
            ]
          : [{ type: "Region" as const, id: "LIST" }],
    }),

    getRegion: builder.query<RegionItemResponse, string>({
      query: (id) => `/regions/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Region" as const, id }],
    }),

    getRegionRypUsers: builder.query<RypUsersResponse, void>({
      query: () => "/regions/ryps",
      providesTags: [{ type: "Region" as const, id: "RYP_USERS" }],
    }),

    getRegionPypUsers: builder.query<RypUsersResponse, void>({
      query: () => "/regions/pyps",
      providesTags: [{ type: "Region" as const, id: "PYP_USERS" }],
    }),

    createRegion: builder.mutation<
      RegionItemResponse,
      { name: string; address: string }
    >({
      query: (body) => ({ url: "/regions", method: "POST", body }),
      invalidatesTags: [{ type: "Region" as const, id: "LIST" }],
    }),

    updateRegion: builder.mutation<
      RegionItemResponse,
      { id: string; name?: string; address?: string; ryp?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/regions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Region" as const, id },
        { type: "Region" as const, id: "LIST" },
        { type: "Region" as const, id: "RYP_USERS" },
      ],
    }),

    archiveRegion: builder.mutation<RegionItemResponse, string>({
      query: (id) => ({ url: `/regions/${id}/archive`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Region" as const, id },
        { type: "Region" as const, id: "LIST" },
        { type: "Region" as const, id: "RYP_USERS" },
      ],
    }),

    unarchiveRegion: builder.mutation<RegionItemResponse, string>({
      query: (id) => ({ url: `/regions/${id}/unarchive`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Region" as const, id },
        { type: "Region" as const, id: "LIST" },
      ],
    }),

    // ── Provinces — authenticated (full records for admin) ───────────

    getProvinces: builder.query<
      ProvinceListResponse,
      { regionId: string; archived?: boolean }
    >({
      query: ({ regionId, archived }) => ({
        url: `/regions/${regionId}/provinces`,
        params:
          typeof archived === "boolean"
            ? { archived: String(archived) }
            : undefined,
      }),
      providesTags: (_result, _error, { regionId }) => [
        { type: "Province" as const, id: `REGION_${regionId}` },
        { type: "Province" as const, id: "LIST" },
      ],
    }),

    createProvince: builder.mutation<
      ProvinceItemResponse,
      { regionId: string; name: string; pyp?: string }
    >({
      query: ({ regionId, ...body }) => ({
        url: `/regions/${regionId}/provinces`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { regionId }) => [
        { type: "Province" as const, id: `REGION_${regionId}` },
        { type: "Region" as const, id: regionId },
        { type: "Region" as const, id: "LIST" },
      ],
    }),

    updateProvince: builder.mutation<
      ProvinceItemResponse,
      { regionId: string; provinceId: string; name?: string; pyp?: string }
    >({
      query: ({ regionId, provinceId, ...body }) => ({
        url: `/regions/${regionId}/provinces/${provinceId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { regionId, provinceId }) => [
        { type: "Province" as const, id: `REGION_${regionId}` },
        { type: "Province" as const, id: provinceId },
        { type: "Region" as const, id: regionId },
        { type: "Region" as const, id: "LIST" },
        { type: "Region" as const, id: "PYP_USERS" },
      ],
    }),

    archiveProvince: builder.mutation<
      ProvinceItemResponse,
      { regionId: string; provinceId: string }
    >({
      query: ({ regionId, provinceId }) => ({
        url: `/regions/${regionId}/provinces/${provinceId}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { regionId }) => [
        { type: "Province" as const, id: `REGION_${regionId}` },
        { type: "Region" as const, id: regionId },
        { type: "Region" as const, id: "LIST" },
      ],
    }),

    unarchiveProvince: builder.mutation<
      ProvinceItemResponse,
      { regionId: string; provinceId: string }
    >({
      query: ({ regionId, provinceId }) => ({
        url: `/regions/${regionId}/provinces/${provinceId}/unarchive`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { regionId }) => [
        { type: "Province" as const, id: `REGION_${regionId}` },
        { type: "Region" as const, id: regionId },
        { type: "Region" as const, id: "LIST" },
      ],
    }),

    // ── Province public lookup — no auth, used by OnboardingModal ───

    /**
     * GET /api/regions/:id/provinces/public
     * Returns active (non-archived) provinces for a region.
     * No auth token required — safe for the onboarding modal which fires
     * before the user has a programme profile set up.
     */
    getPublicProvinces: builder.query<PublicProvinceListResponse, string>({
      query: (regionId) => `/regions/${regionId}/provinces/public`,
      providesTags: (_result, _error, regionId) => [
        { type: "Province" as const, id: `PUBLIC_${regionId}` },
      ],
    }),

    // ── Onboarding mutations ─────────────────────────────────────────

    /**
     * PATCH /api/regions/complete-onboarding
     * Saves the user's region + province selection and sets
     * hasCompletedOnboarding = true on the User document.
     *
     * Body accepted by the backend: { region, province? }
     * (plain name strings, matching the document 9 handler)
     */
    completeOnboarding: builder.mutation<
      OnboardingResponse,
      { region: string; province?: string; interests?: string[] }
    >({
      query: (body) => ({
        url: "/regions/complete-onboarding",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "User" as const, id: "CURRENT" }],
    }),

    /**
     * PATCH /api/regions/dismiss-welcome
     * Sets hasSeenWelcome = true on the User document.
     */
    dismissWelcome: builder.mutation<DismissWelcomeResponse, void>({
      query: () => ({ url: "/regions/dismiss-welcome", method: "PATCH" }),
      invalidatesTags: [{ type: "User" as const, id: "CURRENT" }],
    }),
  }),
});

export const {
  // Regions
  useGetRegionsQuery,
  useGetRegionQuery,
  useGetRegionRypUsersQuery,
  useGetRegionPypUsersQuery,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useArchiveRegionMutation,
  useUnarchiveRegionMutation,
  // Provinces (auth)
  useGetProvincesQuery,
  useCreateProvinceMutation,
  useUpdateProvinceMutation,
  useArchiveProvinceMutation,
  useUnarchiveProvinceMutation,
  // Province public (no auth — OnboardingModal)
  useGetPublicProvincesQuery,
  // Onboarding
  useCompleteOnboardingMutation,
  useDismissWelcomeMutation,
} = regionApi;
